import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { 
  FiPackage, 
  FiLayers, 
  FiActivity, 
  FiDollarSign, 
  FiAlertTriangle, 
  FiSave, 
  FiArrowLeft, 
  FiCheckCircle,
  FiRefreshCw
} from "react-icons/fi";
import { 
  addRawMaterial, 
  updateRawMaterial, 
  getRawMaterialById 
} from "../services/rawMaterialService";
import { getCategories } from "../services/categoryService";
import { getUnits } from "../services/unitService";

const AddRawMaterialLayer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    name: "",
    category: "",
    unit: "",
    rate_per_unit: "",
    consumption_check: 0,
    consumption_unit: "",
    conversion_rate: "",
    rate_per_consumption_unit: "",
    opening_stock: "",
    alert_level: "",
  });

  useEffect(() => {
    loadCategories();
    loadUnits();
    if (isEdit) loadMaterial();
  }, [id]);

  const loadCategories = async () => {
    try {
      const res = await getCategories();
      if (res?.status === "success") {
        setCategories(res.categories || []);
      }
    } catch {
      toast.error("Failed to sync structural categories");
    }
  };

  const loadUnits = async () => {
    try {
      const res = await getUnits();
      if (res?.status === "success") {
        setUnits(res.data || []);
      }
    } catch {
      toast.error("Failed to sync measurement units");
    }
  };

  const loadMaterial = async () => {
    try {
      const res = await getRawMaterialById(id);
      if (res?.status === "success") {
        const m = res.data;
        setForm({
          name: m.name ?? "",
          category: m.category_id ?? m.category ?? "",
          unit: m.unit_id ?? m.unit ?? "",
          rate_per_unit: m.rate_per_unit ?? "",
          consumption_check: Number(m.consumption_check) ?? 0,
          consumption_unit: m.consumption_unit_id ?? m.consumption_unit ?? "",
          conversion_rate: m.conversion_rate ?? "",
          rate_per_consumption_unit: m.rate_per_consumption_unit ?? "",
          opening_stock: m.opening_stock ?? "",
          alert_level: m.alert_level ?? "",
        });
      }
    } catch {
      toast.error("Failed to interrogative asset details");
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (checked ? 1 : 0) : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.name) e.name = "Asset name required";
    if (!form.category) e.category = "Structural category required";
    if (!form.unit) e.unit = "Base unit required";
    if (!form.rate_per_unit) e.rate_per_unit = "Financial rate required";
    if (form.consumption_check === 1) {
      if (!form.consumption_unit) e.consumption_unit = "Consumption matrix unit required";
      if (!form.conversion_rate) e.conversion_rate = "Algorithmic conversion rate required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
        toast.error("Coordinate validation failed");
        return;
    }

    setLoading(true);
    try {
      const res = isEdit
        ? await updateRawMaterial(id, form)
        : await addRawMaterial(form);

      if (res?.status === "success") {
        toast.success(res.message || "Asset synchronized successfully");
        navigate("/raw-materials");
      } else {
        toast.error(res?.message || "Sync failed");
      }
    } catch {
      toast.error("Intelligence Link Error");
    }
    setLoading(false);
  };

  return (
    <div className="container mt-4 mb-5 px-3 px-lg-0">
      <div className="row justify-content-center">
        <div className="col-12 col-xl-10">
          <div className="card shadow-premium border-0 animate-fade-in-up overflow-hidden rounded-4">
            {/* HEADER */}
            <div className="card-header bg-primary py-4 px-4 border-0 d-flex justify-content-between align-items-center">
               <div className="d-flex align-items-center gap-3 text-white">
                  <div className="bg-white bg-opacity-20 p-2 rounded-3">
                     <FiPackage size={24} />
                  </div>
                  <div>
                     <h4 className="mb-0 fw-bold">{isEdit ? "Refine Material Asset" : "Initialize Material Asset"}</h4>
                     <p className="extra-small opacity-75 mb-0">Systemic cataloging of raw logistical inputs</p>
                  </div>
               </div>
               <Link to="/raw-materials" className="btn btn-white-soft rounded-pill btn-sm px-3 fw-bold">
                  <FiArrowLeft className="me-2"/> Back to Inventory
               </Link>
            </div>

            <div className="card-body p-4 p-md-5 bg-white">
              <form onSubmit={handleSubmit} className="premium-modern-form">
                <div className="row g-4">
                  {/* BASIC INFO SECTION */}
                  <div className="col-12 mb-2">
                     <h6 className="fw-bold text-primary d-flex align-items-center gap-2 mb-0">
                        <span className="bg-primary-soft p-1 rounded-2"><FiLayers size={14}/></span>
                        Primary Identification
                     </h6>
                     <hr className="opacity-10" />
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <label className="form-label text-muted small fw-bold">Material Name *</label>
                    <input
                      name="name"
                      placeholder="e.g. High Grade Component X"
                      value={form.name}
                      onChange={handleChange}
                      className={`form-control border-0 bg-light-soft rounded-3 px-3 py-2 ${errors.name ? 'is-invalid' : ''}`}
                    />
                    {errors.name && <div className="invalid-feedback extra-small">{errors.name}</div>}
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <label className="form-label text-muted small fw-bold">Structural Category *</label>
                    <select
                      name="category"
                      value={form.category}
                      onChange={handleChange}
                      className={`form-select border-0 bg-light-soft rounded-3 px-3 py-2 ${errors.category ? 'is-invalid' : ''}`}
                    >
                      <option value="">Select Category</option>
                      {categories.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                    </select>
                    {errors.category && <div className="invalid-feedback extra-small">{errors.category}</div>}
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <label className="form-label text-muted small fw-bold">Base Measurement Unit *</label>
                    <select
                      name="unit"
                      value={form.unit}
                      onChange={handleChange}
                      className={`form-select border-0 bg-light-soft rounded-3 px-3 py-2 ${errors.unit ? 'is-invalid' : ''}`}
                    >
                      <option value="">Select Unit</option>
                      {units.map((u) => (<option key={u.id} value={u.id}>{u.unit_name}</option>))}
                    </select>
                    {errors.unit && <div className="invalid-feedback extra-small">{errors.unit}</div>}
                  </div>

                  {/* LOGISTICS & FINANCE SECTION */}
                  <div className="col-12 mt-5 mb-2">
                     <h6 className="fw-bold text-primary d-flex align-items-center gap-2 mb-0">
                        <span className="bg-primary-soft p-1 rounded-2"><FiDollarSign size={14}/></span>
                        Financial & Stock Parameters
                     </h6>
                     <hr className="opacity-10" />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted small fw-bold">Rate Per Base Unit *</label>
                    <div className="input-group">
                       <span className="input-group-text border-0 bg-light-soft text-muted">₹</span>
                       <input
                        type="number"
                        name="rate_per_unit"
                        placeholder="0.00"
                        value={form.rate_per_unit}
                        onChange={handleChange}
                        className={`form-control border-0 bg-light-soft rounded-end-3 px-3 py-2 ${errors.rate_per_unit ? 'is-invalid' : ''}`}
                      />
                    </div>
                    {errors.rate_per_unit && <div className="text-danger extra-small mt-1">{errors.rate_per_unit}</div>}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted small fw-bold">Opening Stock Level</label>
                    <input
                      type="number"
                      name="opening_stock"
                      placeholder="0"
                      value={form.opening_stock}
                      onChange={handleChange}
                      className="form-control border-0 bg-light-soft rounded-3 px-3 py-2"
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted small fw-bold d-flex align-items-center gap-2">
                       Alert Threshold <FiAlertTriangle className="text-warning" size={14}/>
                    </label>
                    <input
                      type="number"
                      name="alert_level"
                      placeholder="Min. stock for alert"
                      value={form.alert_level}
                      onChange={handleChange}
                      className="form-control border-0 bg-light-soft rounded-3 px-3 py-2"
                    />
                  </div>

                  {/* DYNAMIC CONSUMPTION MATRIX SECTION */}
                  <div className="col-12 mt-5 mb-2">
                     <div className="form-check form-switch p-0 d-flex align-items-center gap-3">
                        <h6 className="fw-bold text-primary d-flex align-items-center gap-2 mb-0">
                           <span className="bg-primary-soft p-1 rounded-2"><FiRefreshCw size={14}/></span>
                           Consumption Logistics
                        </h6>
                        <input
                          type="checkbox"
                          className="form-check-input ms-0 mt-0"
                          name="consumption_check"
                          style={{width: '2.5rem', height: '1.25rem'}}
                          checked={form.consumption_check === 1}
                          onChange={handleChange}
                        />
                        <span className="extra-small text-muted fw-bold">Activate Differential Unit Matrix</span>
                     </div>
                     <hr className="opacity-10" />
                  </div>

                  {form.consumption_check === 1 && (
                    <div className="col-12 animate-fade-in">
                       <div className="row g-4 p-4 rounded-4 bg-light-soft border border-dashed border-primary border-opacity-25">
                          <div className="col-md-4">
                            <label className="form-label text-muted small fw-bold">Consumption Unit *</label>
                            <select
                              name="consumption_unit"
                              value={form.consumption_unit}
                              onChange={handleChange}
                              className={`form-select border-0 bg-white rounded-3 px-3 py-2 ${errors.consumption_unit ? 'is-invalid' : ''}`}
                            >
                              <option value="">Select Unit</option>
                              {units.map((u) => (<option key={u.id} value={u.id}>{u.unit_name}</option>))}
                            </select>
                            {errors.consumption_unit && <div className="invalid-feedback extra-small">{errors.consumption_unit}</div>}
                          </div>

                          <div className="col-md-4">
                            <label className="form-label text-muted small fw-bold">Algorithmic Conversion Factor *</label>
                            <input
                              type="number"
                              name="conversion_rate"
                              placeholder="e.g. 1000"
                              value={form.conversion_rate}
                              onChange={handleChange}
                              className={`form-control border-0 bg-white rounded-3 px-3 py-2 ${errors.conversion_rate ? 'is-invalid' : ''}`}
                            />
                            {errors.conversion_rate && <div className="invalid-feedback extra-small">{errors.conversion_rate}</div>}
                          </div>

                          <div className="col-md-4">
                            <label className="form-label text-muted small fw-bold">Derived Rate / Consumption Unit</label>
                            <div className="input-group">
                               <span className="input-group-text border-0 bg-white text-muted opacity-50">₹</span>
                               <input
                                  type="number"
                                  name="rate_per_consumption_unit"
                                  placeholder="Auto Calculated"
                                  value={form.rate_per_consumption_unit}
                                  onChange={handleChange}
                                  className="form-control border-0 bg-white rounded-end-3 px-3 py-2"
                                />
                            </div>
                          </div>
                       </div>
                    </div>
                  )}

                  {/* SUBMIT ACTION */}
                  <div className="col-12 mt-5">
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn btn-primary-gradient w-100 py-3 rounded-pill shadow-sm fw-bold d-flex align-items-center justify-content-center gap-2"
                    >
                      {loading ? (
                        <div className="spinner-border spinner-border-sm" role="status"></div>
                      ) : (
                        <>
                          <FiSave />
                          {isEdit ? "Synchronize Asset Data" : "Deploy Material to Matrix"}
                        </>
                      )}
                    </button>
                    
                    <div className="text-center mt-3">
                       <span className="extra-small text-muted d-flex align-items-center justify-content-center gap-2">
                          <FiCheckCircle className="text-success"/> Logistical compliance validated
                       </span>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddRawMaterialLayer;
